// utils/turnstile.ts

import { onAuthenticationFailed } from '../events/authenticationEvent';
import { dispatchNotification, NotificationType } from '../events/notificationEvent';
import { AuthenticationType } from './authenticationType';
import { registerSubmitGate, SubmitGate } from './submitGate';

interface TurnstileRenderOptions {
	'sitekey': string;
	'size'?: 'normal' | 'flexible' | 'compact';
	'appearance'?: TurnstileAppearance;
	'theme'?: 'light' | 'dark' | 'auto';
	'refresh-expired'?: 'auto' | 'manual' | 'never';
	'retry'?: 'auto' | 'never';
	'callback'?: (token: string) => void;
	'error-callback'?: () => void;
	'expired-callback'?: () => void;
	'before-interactive-callback'?: () => void;
	'after-interactive-callback'?: () => void;
}

export type TurnstileResult = 'verified' | 'failed' | 'interaction-required';
export type TurnstileAppearance = 'always' | 'execute' | 'interaction-only';

/**
 * The methods exposed by the Cloudflare Turnstile script.
 */
declare global {
	interface Window {
		turnstile?: {
			render: (container: string, options: TurnstileRenderOptions) => string | undefined;
			reset: (widgetId?: string) => void;
		};
	}
}

/**
 * Displays feedback that turnstile could not verify the user isn't a bot.
 */
export function notifyTurnstileFailure(): void {
	dispatchNotification(
		NotificationType.error,
		'Error',
		window.serverData?.turnstileErrorMessage ?? "Sorry, we couldn't verify you're not a robot",
	);
}

/**
 * Displays feedback that turnstile interaction is required to verify the user isn't a bot.
 */
export function notifyTurnstileVerification(): void {
	dispatchNotification(
		NotificationType.error,
		'Verification required',
		window.serverData?.turnstileInteractionMessage ?? "Please verify you're not a robot",
	);
}

/**
 * A round of verification. A widget may perform multiple verifications. For example,
 * if authentication fails or the verification times out.
 */
interface Round {
	// The promise a submit waits on
	readonly settled: Promise<TurnstileResult>;
	// The function to call to resolve the promise
	readonly settle: (result: TurnstileResult) => void;
}

/**
 * Arms a fresh round of verification.
 *
 * @returns {Round} The armed round.
 */
function armRound(): Round {
	// settle is always assigned from the promise constructor below so we can treat it as non-nullable
	let settle!: (result: TurnstileResult) => void;

	const settled = new Promise<TurnstileResult>((resolve) => {
		settle = resolve;
	});

	return { settled, settle };
}

/**
 * Arms a round of verification that has already been answered.
 *
 * @param {TurnstileResult} result The answer to give.
 * @returns {Round} The settled round.
 */
function settledRound(result: TurnstileResult): Round {
	const round = armRound();
	round.settle(result);
	return round;
}

/**
 * Details fo the widget Cloudflare has rendered.
 */
class RenderedWidget {
	private constructor(
		/** The ID Cloudflare assigned to the widget. */
		private readonly widgetId: string,

		/** The container the widget was rendered into. */
		readonly container: HTMLElement,
	) {}

	/**
	 * Renders a Cloudflare widget into the container.
	 *
	 * @param {HTMLElement} container The container to render into.
	 * @param {TurnstileRenderOptions} options The options to render with.
	 * @returns {RenderedWidget | null} The widget, or null if Cloudflare declined to render one.
	 */
	static render(container: HTMLElement, options: TurnstileRenderOptions): RenderedWidget | null {
		const widgetId = window.turnstile?.render(`#${container.id}`, options);

		if (!widgetId) return null;

		return new RenderedWidget(widgetId, container);
	}

	/**
	 * Performs a new verification and issues another token.
	 */
	reset(): void {
		window.turnstile?.reset(this.widgetId);
	}
}

/**
 * Encapsulates a form's Turnstile widget, state and behaviour.
 * <p>
 * Converts Cloudflare Turnstile's callback driven verification lifecycle into a Promise based
 * state machine, so form submission can simply await verification while correctly handling
 * success, errors, token expiry, and interactive challenges.
 */
class TurnstileWidget {
	/** The widget on the page, if Cloudflare has rendered one for this form. */
	private widget: RenderedWidget | null = null;

	/** Answered until something renders, so a form Turnstile does not gate can still submit. */
	private round = settledRound('verified');

	/** Whether the current token has expired or been spent, and needs refreshing. */
	private needsRefresh = false;

	/** Whether Turnstile is waiting for the customer to solve a challenge. */
	private interactionPending = false;

	/** The gate on this form's submit button. Nothing to gate until a render is called. */
	private gate: SubmitGate = { evaluate: () => undefined };

	constructor(private readonly type: AuthenticationType) {
		onAuthenticationFailed(type, () => this.reset());
	}

	/**
	 * Resolves once this form's verification has settled.
	 * Resolves to 'verified' immediately if Turnstile is disabled.
	 *
	 * @returns {Promise<TurnstileResult>} A promise containing the current Turnstile verification result.
	 */
	wait(): Promise<TurnstileResult> {
		return this.round.settled;
	}

	/**
	 * Triggers a fresh verification via turnstile.reset() if the token has expired or been spent.
	 */
	refreshIfNeeded(): void {
		if (!this.needsRefresh) return;

		this.needsRefresh = false;
		this.widget?.reset();
	}

	/**
	 * Renders the Cloudflare widget for this form.
	 */
	render(): void {
		const siteKey = window.serverData?.turnstileSiteKey;
		if (!siteKey) return;

		// The Cloudflare script is deferred but checkout.ts isn't so we may need to add a listener to wait for
		// it to load before we can render.
		if (!window.turnstile) {
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.render(), { once: true });
			}

			return;
		}

		const container = document.getElementById(`${this.type}-turnstile-widget`);
		if (!container) return;

		// Ensure we're not attempting to render a widget on a container that already has one.
		// Cloudflare will not render a second widget into the same container
		if (this.widget?.container === container) return;

		this.gateSubmitButton(container);
		this.arm(false);

		const appearance: TurnstileAppearance = window.serverData?.turnstileForceVisible
			? 'always'
			: 'interaction-only';

		this.widget = RenderedWidget.render(container, this.renderOptions(siteKey, appearance));

		// Cloudflare declined, so none of the callbacks below will ever fire. Settle the round we
		// just armed rather than leaving a submit to wait on it for good.
		if (!this.widget) this.round.settle('failed');
	}

	/**
	 * Puts the form back to waiting on a verification.
	 *
	 * @param {boolean} needsRefresh Whether the next submit should ask Turnstile for a new token.
	 */
	arm(needsRefresh: boolean): void {
		this.round = armRound();
		this.needsRefresh = needsRefresh;
		this.interactionPending = false;
		this.gate.evaluate();
	}

	/**
	 * Points the gate at the submit button of the form this container sits in, registering this
	 * widget's condition against it the first time that button is seen.
	 *
	 * @param {HTMLElement} container The container the widget is being rendered into.
	 */
	private gateSubmitButton(container: HTMLElement): void {
		const submitBtn = container
			.closest('form')
			?.querySelector(
				'button[type="submit"], input[type="submit"]',
			) as HTMLButtonElement | null;

		if (submitBtn) {
			this.gate = registerSubmitGate(submitBtn, () => !this.interactionPending);
		}
	}

	/**
	 * Reset the widget and perform a fresh verification.
	 */
	private reset(): void {
		if (!this.widget) return;

		this.arm(false);
		this.widget.reset();
	}

	/**
	 * The options handed to Cloudflare, wiring its verification lifecycle to this form.
	 * <p>
	 * This translates callbacks into the promise that form submission has to wait for.
	 * There are three callbacks that resolve the promise:
	 * <ul>
	 * <li>callback: verification succeeded, resolve to 'verified'</li>
	 * <li>error-callback: verification failed, resolve to 'failed'</li>
	 * <li>before-interactive-callback: a challenge is required, resolve to 'interaction-required'</li>
	 * </ul>
	 * @param {string} siteKey The sitekey configured in Cloudflare for this domain.
	 * @param {TurnstileAppearance} appearance Whether the widget should always be visible,
	 * or only when a challenge is required.
	 * @returns {TurnstileRenderOptions} The options to render with.
	 */
	private renderOptions(
		siteKey: string,
		appearance: TurnstileAppearance,
	): TurnstileRenderOptions {
		return {
			'sitekey': siteKey,
			'size': 'normal',
			'appearance': appearance,
			'theme': 'light',
			'refresh-expired': 'manual',
			'retry': 'never',

			// A round settles once, so a later callback on the same round changes nothing:
			// recovering takes an expiry, a rejected attempt, or a fresh render.
			'callback': () => {
				this.round.settle('verified');
			},

			'error-callback': () => {
				this.round.settle('failed');
			},

			// When a token has expired reset the promise so a new token will be requested on submit
			'expired-callback': () => {
				this.arm(true);
			},

			// When a challenge is required solve the submit gate so the form can't be submitted.
			// Resolve the promise with 'interaction-required' and reset a new promise to wait
			// on the result of the interaction.
			'before-interactive-callback': () => {
				this.round.settle('interaction-required');
				this.round = armRound();

				this.interactionPending = true;
				this.gate.evaluate();
			},

			// Once the challenge is solved open the submit gate so the form can be submitted.
			'after-interactive-callback': () => {
				this.interactionPending = false;
				this.gate.evaluate();
			},
		};
	}
}

const widgets = new Map<AuthenticationType, TurnstileWidget>();

/**
 * Returns the widget for an authentication type, creating it on first use.
 *
 * @param {AuthenticationType} type 'login' or 'register'.
 * @returns {TurnstileWidget} tile widget for the given form.
 */
function widgetFor(type: AuthenticationType): TurnstileWidget {
	let widget = widgets.get(type);

	if (!widget) {
		widget = new TurnstileWidget(type);
		widgets.set(type, widget);
	}

	return widget;
}

/**
 * Resolves once the given form's Turnstile widget has settled with its current token.
 *
 * @param {AuthenticationType} type Identifies which form's widget to wait for.
 * @returns {Promise<TurnstileResult>} The current Turnstile verification result.
 */
export function waitForTurnstile(type: AuthenticationType): Promise<TurnstileResult> {
	return widgetFor(type).wait();
}

/**
 * Locks/unlocks every fieldset in the form, deliberately excluding the Turnstile widget itself.
 *
 * @param {HTMLFormElement} form The form whose fields should be locked/unlocked.
 * @param {boolean} locked True to lock the fields, false to unlock them.
 */
function setFieldsLocked(form: HTMLFormElement, locked: boolean): void {
	form.querySelectorAll('fieldset').forEach((fieldset) => {
		fieldset.inert = locked;
	});
}

/**
 * Intercepts the form submission and waits for Turnstile verification to complete
 * before performing the actual submission.
 *
 * If the token has timed out, a new token will be requested before submission.
 * If an interactive challenge is required, the current submission is cancelled.
 *
 * @param {HTMLFormElement} form The form being submitted; its submit button gets the loading state.
 * @param {AuthenticationType} tab Identifies which form's widget to wait for.
 * @param {() => void | Promise<void>} submit Performs the actual submission once verified.
 * @param {boolean} navigatesAway True for a submit that posts the whole page, false for async requests
 * @returns {(e: Event) => Promise<void>} An event handler for the form's submit listener.
 */
export function withTurnstile(
	form: HTMLFormElement,
	tab: AuthenticationType,
	submit: () => void | Promise<void>,
	navigatesAway = false,
): (e: Event) => Promise<void> {
	let submitting = false;

	return async (e: Event) => {
		e.preventDefault();

		if (submitting) return;

		const submitBtn = form.querySelector('.form__submit') as HTMLButtonElement | null;

		const release = () => {
			submitting = false;
			submitBtn?.classList.remove('btn-loading');
			setFieldsLocked(form, false);
		};

		try {
			submitting = true;
			submitBtn?.classList.add('btn-loading');

			setFieldsLocked(form, true);

			// The verification may have timed out, trigger a verification if needed
			widgetFor(tab).refreshIfNeeded();

			// Wait for the Turnstile request to complete before actually submitting the form.
			const result = await waitForTurnstile(tab);

			if (result === 'verified') {
				await submit();

				// With a full page reload the form will still be submitting at this point,
				// we don't need to release because the page will be reloaded. For an AJAX style
				// submission submit won't complete until the response has been processed and
				// we need to release manually
				if (!navigatesAway) release();
			} else {
				release();

				if (result == 'interaction-required') {
					notifyTurnstileVerification();
				} else if (result === 'failed') {
					notifyTurnstileFailure();
				}
			}
		} catch (err) {
			console.error('❌ Failed to submit form:', err);
			release();
			throw err;
		}
	};
}

/**
 * Renders the login and register Turnstile widgets.
 *
 * There are two independent widgets, one for each tab/form.
 */
export function initTurnstile(): void {
	widgetFor('login').render();
	widgetFor('register').render();
}
